import { useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import type { ITheme } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';

// xterm.js is loaded lazily so it never lands in the main admin bundle -- this
// page is the only thing in the app that needs a terminal.
async function loadXterm() {
  const [{ Terminal }, { FitAddon }] = await Promise.all([
    import('@xterm/xterm'),
    import('@xterm/addon-fit'),
  ]);
  return { Terminal, FitAddon };
}

const COLUMNS = 120;
const PADDING_PX = 16;
const BACKGROUND = '#f7f5fc';

// Light, purple-tinted to sit inside a light-mode form rather than the usual
// black terminal. Modelled on the range a conventional light terminal theme
// gives ANSI codes (GitHub's light theme, roughly) rather than collapsing
// everything toward one purple: black/white/brightBlack/brightWhite form a
// real four-step scale (bold headings darkest, dimmed detail lines lightest),
// and red/green/yellow/blue keep their conventional error/success/warn/info
// meaning so listr2's own output still reads correctly.
const THEME: ITheme = {
  background: BACKGROUND,
  foreground: '#24213b',
  cursor: '#563fd8',
  cursorAccent: BACKGROUND,
  selectionBackground: '#ddd9f7',
  black: '#24213b',
  brightBlack: '#8b87a3',
  red: '#c5303e',
  brightRed: '#e0495a',
  green: '#2f8a5b',
  brightGreen: '#3fae78',
  yellow: '#b9860f',
  brightYellow: '#d6a52e',
  blue: '#3062c9',
  brightBlue: '#4d7fe0',
  magenta: '#6b46c1',
  brightMagenta: '#8b5cf6',
  cyan: '#2c8a8f',
  brightCyan: '#38a8ae',
  white: '#5d5876',
  brightWhite: '#150f28',
};

/**
 * A read-only terminal that renders chunks of raw terminal output (ANSI
 * colours, cursor movement, spinners) as they arrive.
 *
 * Fixed to 80 columns wide -- the CLI it mirrors wraps at that width -- with
 * height flexing to fill its container. The width is measured from xterm's
 * own rendered cell size rather than assumed, so it's exact for whatever font
 * metrics the browser actually uses.
 *
 * `chunks` is expected to only grow -- each new element is written to the
 * terminal without touching what's already rendered.
 */
export function SeedTerminal({ chunks }: { chunks: string[] }): React.ReactElement {
  const screenRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<import('@xterm/xterm').Terminal | null>(null);
  const fitRef = useRef<import('@xterm/addon-fit').FitAddon | null>(null);
  const writtenRef = useRef(0);
  const [ready, setReady] = useState(false);
  const [terminalWidth, setTerminalWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    let disposed = false;

    void loadXterm().then(({ Terminal, FitAddon }) => {
      if (disposed || !screenRef.current) {
        return;
      }

      const terminal = new Terminal({
        cols: COLUMNS,
        convertEol: true,
        disableStdin: true,
        fontSize: 12,
        theme: THEME,
      });
      const fitAddon = new FitAddon();
      terminal.loadAddon(fitAddon);
      terminal.open(screenRef.current);

      // The rendered width for a fixed 80-column terminal, measured from
      // xterm's own layout rather than computed from font metrics we'd
      // otherwise have to guess.
      setTerminalWidth(terminal.element?.offsetWidth);

      terminalRef.current = terminal;
      fitRef.current = fitAddon;
      setReady(true);
    });

    return () => {
      disposed = true;
      terminalRef.current?.dispose();
      terminalRef.current = null;
      writtenRef.current = 0;
    };
    // Mounted once per modal open; SeedsPage remounts this component per run
    // by keying it on the run.
  }, []);

  useEffect(() => {
    const terminal = terminalRef.current;
    if (!ready || !terminal) {
      return;
    }
    for (; writtenRef.current < chunks.length; writtenRef.current++) {
      terminal.write(chunks[writtenRef.current] ?? '');
    }
  }, [ready, chunks]);

  useEffect(() => {
    const fitRows = () => {
      const terminal = terminalRef.current;
      const proposed = fitRef.current?.proposeDimensions();
      if (terminal && proposed?.rows) {
        // Columns stay fixed at 80; only the row count tracks the container.
        terminal.resize(COLUMNS, proposed.rows);
      }
    };

    fitRows();
    window.addEventListener('resize', fitRows);
    return () => window.removeEventListener('resize', fitRows);
  }, [ready]);

  return (
    <Box
      bg={BACKGROUND}
      borderWidth="1px"
      borderColor="#ddd9f7"
      borderRadius="md"
      h="full"
      flexShrink={0}
      w={typeof terminalWidth === 'number' ? `${terminalWidth + PADDING_PX * 2}px` : undefined}
      p={`${PADDING_PX}px`}
      overflow="hidden"
    >
      <Box ref={screenRef} h="full" w="fit-content" />
    </Box>
  );
}
